import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { format } from 'date-fns';
import {
  Hotel,
  ArrowLeft,
  Loader2,
  Check,
  User,
  Clock,
  Plane,
  FileText,
  Accessibility,
  Calendar,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

export default function PreCheckinPage() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [existingData, setExistingData] = useState(null);
  const [form, setForm] = useState({
    id_type: '',
    id_number: '',
    id_expiry: '',
    id_country: '',
    estimated_arrival_time: '',
    flight_number: '',
    special_requests: '',
    room_preferences: {},
    accessibility_needs: '',
    terms_accepted: false,
    privacy_accepted: false,
  });

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    loadData();
  }, [authLoading, isAuthenticated, token, reservationId, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resRes, precheckinRes] = await Promise.all([
        guestPortalApi.getReservation(token, reservationId),
        guestPortalApi.getPreCheckin(token, reservationId),
      ]);
      
      setReservation(resRes.data);
      
      if (precheckinRes.data) {
        setExistingData(precheckinRes.data);
        setForm({
          id_type: precheckinRes.data.id_type || '',
          id_number: precheckinRes.data.id_number || '',
          id_expiry: precheckinRes.data.id_expiry || '',
          id_country: precheckinRes.data.id_country || '',
          estimated_arrival_time: precheckinRes.data.estimated_arrival_time || '',
          flight_number: precheckinRes.data.flight_number || '',
          special_requests: precheckinRes.data.special_requests || '',
          room_preferences: precheckinRes.data.room_preferences || {},
          accessibility_needs: precheckinRes.data.accessibility_needs || '',
          terms_accepted: precheckinRes.data.terms_accepted || false,
          privacy_accepted: precheckinRes.data.privacy_accepted || false,
        });
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load reservation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.terms_accepted || !form.privacy_accepted) {
      toast.error('Please accept the terms and privacy policy');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        id_type: form.id_type || null,
        id_number: form.id_number || null,
        id_expiry: form.id_expiry || null,
        id_country: form.id_country || null,
        estimated_arrival_time: form.estimated_arrival_time || null,
        flight_number: form.flight_number || null,
        special_requests: form.special_requests || null,
        room_preferences: form.room_preferences || {},
        accessibility_needs: form.accessibility_needs || null,
        terms_accepted: form.terms_accepted,
        privacy_accepted: form.privacy_accepted,
      };
      await guestPortalApi.submitPreCheckin(token, reservationId, payload);
      
      toast.success('Pre-check-in completed!');
      navigate('/portal/dashboard');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit pre-check-in');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30" data-testid="pre-checkin-page">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-2">
            <Hotel className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold">HMS Hotels</span>
          </Link>
          <Button variant="ghost" onClick={() => navigate('/portal/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold mb-2">Pre-Check-In</h1>
          <p className="text-muted-foreground mb-8">
            Complete your pre-check-in to expedite your arrival
          </p>

          {/* Reservation Summary */}
          {reservation && (
            <Card className="mb-6 bg-muted/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-background">
                    <Hotel className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{reservation.properties?.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {format(new Date(reservation.arrival_date), 'MMM d')} - {format(new Date(reservation.departure_date), 'MMM d, yyyy')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Confirmation: {reservation.confirmation_number}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {existingData?.status === 'COMPLETE' && (
            <Card className="mb-6 border-success/50 bg-success/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-success">
                  <Check className="h-5 w-5" />
                  <span className="font-medium">Pre-check-in already completed</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  You can update your information below if needed.
                </p>
              </CardContent>
            </Card>
          )}

          <form onSubmit={handleSubmit}>
            {/* ID Information */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  ID Information
                </CardTitle>
                <CardDescription>Required for check-in verification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ID Type</Label>
                    <Select 
                      value={form.id_type} 
                      onValueChange={(v) => setForm({ ...form, id_type: v })}
                    >
                      <SelectTrigger data-testid="id-type-select">
                        <SelectValue placeholder="Select ID type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PASSPORT">Passport</SelectItem>
                        <SelectItem value="NATIONAL_ID">National ID</SelectItem>
                        <SelectItem value="DRIVERS_LICENSE">Driver's License</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>ID Number</Label>
                    <Input
                      value={form.id_number}
                      onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                      placeholder="ID number"
                      data-testid="id-number-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ID Expiry Date</Label>
                    <Input
                      type="date"
                      value={form.id_expiry}
                      onChange={(e) => setForm({ ...form, id_expiry: e.target.value })}
                      data-testid="id-expiry-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country of Issue</Label>
                    <Input
                      value={form.id_country}
                      onChange={(e) => setForm({ ...form, id_country: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="e.g., ET, US, GB"
                      maxLength={2}
                      data-testid="id-country-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Arrival Details */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Arrival Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Estimated Arrival Time</Label>
                    <Select 
                      value={form.estimated_arrival_time} 
                      onValueChange={(v) => setForm({ ...form, estimated_arrival_time: v })}
                    >
                      <SelectTrigger data-testid="arrival-time-select">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="14:00">2:00 PM (Check-in time)</SelectItem>
                        <SelectItem value="15:00">3:00 PM</SelectItem>
                        <SelectItem value="16:00">4:00 PM</SelectItem>
                        <SelectItem value="17:00">5:00 PM</SelectItem>
                        <SelectItem value="18:00">6:00 PM</SelectItem>
                        <SelectItem value="19:00">7:00 PM</SelectItem>
                        <SelectItem value="20:00">8:00 PM</SelectItem>
                        <SelectItem value="21:00">9:00 PM</SelectItem>
                        <SelectItem value="22:00">10:00 PM</SelectItem>
                        <SelectItem value="23:00">11:00 PM</SelectItem>
                        <SelectItem value="00:00">12:00 AM (Midnight)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Flight Number (if applicable)</Label>
                    <div className="relative">
                      <Plane className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        className="pl-10"
                        value={form.flight_number}
                        onChange={(e) => setForm({ ...form, flight_number: e.target.value.toUpperCase() })}
                        placeholder="e.g., ET302"
                        data-testid="flight-number-input"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Special Requests */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Preferences & Requests
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Special Requests</Label>
                  <Textarea
                    value={form.special_requests}
                    onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                    placeholder="Any special requests for your stay (e.g., high floor, extra pillows)..."
                    rows={3}
                    data-testid="special-requests-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Accessibility className="h-4 w-4" />
                    Accessibility Needs
                  </Label>
                  <Textarea
                    value={form.accessibility_needs}
                    onChange={(e) => setForm({ ...form, accessibility_needs: e.target.value })}
                    placeholder="Please describe any accessibility requirements..."
                    rows={2}
                    data-testid="accessibility-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Terms & Conditions */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Terms & Conditions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={form.terms_accepted}
                    onCheckedChange={(checked) => setForm({ ...form, terms_accepted: checked })}
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm leading-relaxed">
                    I agree to the <a href="#" className="text-primary hover:underline">Terms and Conditions</a> of the hotel. I understand the check-in and check-out policies.
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={form.privacy_accepted}
                    onCheckedChange={(checked) => setForm({ ...form, privacy_accepted: checked })}
                    data-testid="privacy-checkbox"
                  />
                  <label htmlFor="privacy" className="text-sm leading-relaxed">
                    I agree to the <a href="#" className="text-primary hover:underline">Privacy Policy</a> and consent to the processing of my personal data.
                  </label>
                </div>
              </CardContent>
              <CardFooter className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate('/portal/dashboard')}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting || !form.terms_accepted || !form.privacy_accepted}
                  data-testid="submit-precheckin-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Complete Pre-Check-In
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </main>
    </div>
  );
}
